/**
 * Logger - Web-compatible logging utility
 *
 * Provides structured logging with categories and levels.
 * Uses console methods instead of electron-log for web compatibility.
 */
/* eslint-disable no-console */

import { LogLevel, LogCategory, LogEntry } from "./loggerTypes";

class Logger {
  private sessionId: string;
  private isProduction: boolean;
  private debugEnabled: boolean;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.isProduction = import.meta.env?.PROD ?? process.env.NODE_ENV === "production";
    this.debugEnabled = !this.isProduction;
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private formatLog(entry: LogEntry): string {
    const logObject = {
      ...entry,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
    };
    return JSON.stringify(logObject);
  }

  private formatConsoleMessage(entry: LogEntry): string {
    const time = new Date().toLocaleTimeString();
    return `${time} [${entry.category}] ${entry.operation}: ${entry.message}`;
  }

  // Main logging methods
  public error(
    category: LogCategory,
    operation: string,
    message: string,
    context?: Record<string, unknown>,
    error?: Error
  ): void {
    const entry: LogEntry = {
      level: LogLevel.ERROR,
      category,
      operation,
      message,
      context: this.sanitizeContext(context),
      error: error
        ? {
            message: error.message,
            stack: error.stack,
            code: (error as Error & { code?: string }).code,
          }
        : undefined,
    };

    if (this.debugEnabled) {
      console.error(this.formatConsoleMessage(entry), context, error);
    } else {
      console.error(this.formatLog(entry));
    }
  }

  public warn(
    category: LogCategory,
    operation: string,
    message: string,
    context?: Record<string, unknown>
  ): void {
    const entry: LogEntry = {
      level: LogLevel.WARN,
      category,
      operation,
      message,
      context: this.sanitizeContext(context),
    };

    if (this.debugEnabled) {
      console.warn(this.formatConsoleMessage(entry), context);
    } else {
      console.warn(this.formatLog(entry));
    }
  }

  public info(
    category: LogCategory,
    operation: string,
    message: string,
    context?: Record<string, unknown>
  ): void {
    const entry: LogEntry = {
      level: LogLevel.INFO,
      category,
      operation,
      message,
      context: this.sanitizeContext(context),
    };

    if (this.debugEnabled) {
      console.info(this.formatConsoleMessage(entry), context);
    } else {
      console.info(this.formatLog(entry));
    }
  }

  public debug(
    category: LogCategory,
    operation: string,
    message: string,
    context?: Record<string, unknown>
  ): void {
    // Skip debug logs in production
    if (this.isProduction) return;

    const entry: LogEntry = {
      level: LogLevel.DEBUG,
      category,
      operation,
      message,
      context: this.sanitizeContext(context),
    };

    console.debug(this.formatConsoleMessage(entry), context);
  }

  // Performance tracking
  public startTimer(_operationId: string): () => number {
    const startTime = Date.now();
    return () => Date.now() - startTime;
  }

  public logPerformance(
    category: LogCategory,
    operation: string,
    duration: number,
    context?: Record<string, unknown>
  ): void {
    this.info(category, operation, `Operation completed in ${duration}ms`, {
      ...context,
      duration,
    });
  }

  // Sanitize sensitive data
  private sanitizeContext(context?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!context) return undefined;

    const sanitized = { ...context };

    // Sanitize file paths (keep filename, remove user directory)
    if (sanitized.filePath && typeof sanitized.filePath === "string") {
      sanitized.filePath = this.sanitizePath(sanitized.filePath);
    }

    // Sanitize project IDs (partial for privacy)
    if (sanitized.projectId && typeof sanitized.projectId === "string") {
      const id = sanitized.projectId;
      sanitized.projectId = id.length > 8 ? `${id.slice(0, 8)}...` : id;
    }

    return sanitized;
  }

  private sanitizePath(filePath: string): string {
    // Keep only the last 2 segments of the path
    // Works with both forward and backward slashes
    const separator = filePath.includes("\\") ? "\\" : "/";
    const segments = filePath.split(/[/\\]/);
    return segments.slice(-2).join(separator);
  }
}

// Export singleton instance
export const logger = new Logger();
export { LogCategory };
